import { useEffect, useRef, useState } from 'react';

import { DriverLocation } from '@/components/TrackingDetail/websocket';
import {
  fetchMapboxDrivingDuration,
  formatDurationAsEtaTime,
  parseCoordinate,
} from '@/utils/mapboxDirections';

const REFETCH_MIN_INTERVAL_MS = 60_000;
const MIN_DRIVER_MOVE_KM = 0.2;

const toRadians = (value: number) => (value * Math.PI) / 180;

const getDistanceKm = (
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
) => {
  const earthRadiusKm = 6371;
  const dLat = toRadians(lat2 - lat1);
  const dLng = toRadians(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) *
      Math.cos(toRadians(lat2)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return earthRadiusKm * c;
};

type UseComputedDrivingEtaArgs = {
  enabled: boolean;
  hasApiEta: boolean;
  driverLocation: DriverLocation | null;
  destinationLatitude?: string | number | null;
  destinationLongitude?: string | number | null;
  dateLocale: string;
};

const useComputedDrivingEta = ({
  enabled,
  hasApiEta,
  driverLocation,
  destinationLatitude,
  destinationLongitude,
  dateLocale,
}: UseComputedDrivingEtaArgs) => {
  const [computedEta, setComputedEta] = useState<string | null>(null);
  const lastFetchRef = useRef<{
    time: number;
    driverLat: number;
    driverLng: number;
  } | null>(null);

  useEffect(() => {
    if (!enabled || hasApiEta) {
      setComputedEta(null);
      lastFetchRef.current = null;
      return;
    }

    const destinationLat = parseCoordinate(destinationLatitude);
    const destinationLng = parseCoordinate(destinationLongitude);

    if (!driverLocation || destinationLat == null || destinationLng == null) {
      setComputedEta(null);
      return;
    }

    const { latitude: driverLat, longitude: driverLng } = driverLocation;

    if (
      typeof driverLat !== 'number' ||
      typeof driverLng !== 'number' ||
      isNaN(driverLat) ||
      isNaN(driverLng) ||
      driverLat === 0 ||
      driverLng === 0
    ) {
      setComputedEta(null);
      return;
    }

    const now = Date.now();
    const lastFetch = lastFetchRef.current;

    if (lastFetch) {
      const movedKm = getDistanceKm(
        lastFetch.driverLat,
        lastFetch.driverLng,
        driverLat,
        driverLng
      );
      const elapsedMs = now - lastFetch.time;

      if (elapsedMs < REFETCH_MIN_INTERVAL_MS && movedKm < MIN_DRIVER_MOVE_KM) {
        return;
      }
    }

    const controller = new AbortController();

    fetchMapboxDrivingDuration(
      { latitude: driverLat, longitude: driverLng },
      { latitude: destinationLat, longitude: destinationLng },
      controller.signal
    )
      .then((durationSeconds) => {
        if (controller.signal.aborted || durationSeconds == null) {
          return;
        }

        setComputedEta(formatDurationAsEtaTime(durationSeconds, dateLocale));
        lastFetchRef.current = {
          time: Date.now(),
          driverLat,
          driverLng,
        };
      })
      .catch(() => {
        // Ignore aborted or failed ETA lookups.
      });

    return () => {
      controller.abort();
    };
  }, [
    enabled,
    hasApiEta,
    driverLocation,
    destinationLatitude,
    destinationLongitude,
    dateLocale,
  ]);

  return computedEta;
};

export { useComputedDrivingEta };
